/**
 * get_runs tool - Get flow run history
 */

import { z } from "zod";
import type { FlowManagementApi } from "../api/flow-management.js";
import { logger } from "../utils/logger.js";

export const getRunsInputSchema = z.object({
  flowId: z.string().describe("The flow ID (GUID) to get runs for"),
  environment: z
    .string()
    .optional()
    .describe("Environment name or GUID. Uses default if omitted."),
  top: z
    .number()
    .min(1)
    .max(250)
    .optional()
    .describe("Maximum number of runs to return (default 25)"),
  status: z
    .enum(["Succeeded", "Failed", "Running", "Cancelled", "TimedOut", "Waiting"])
    .optional()
    .describe("Only runs with this status — 'Failed' finds the failures even when the recent page is all green"),
});

export type GetRunsInput = z.infer<typeof getRunsInputSchema>;

export const getRunsTool = {
  name: "get_runs",
  description:
    "Get the execution history of a Power Automate flow. Shows run times, status (Succeeded/Failed/Running), and error information for failed runs. Useful for debugging flow issues.",
  inputSchema: {
    type: "object" as const,
    properties: {
      flowId: {
        type: "string",
        description: "The flow ID (GUID) to get runs for",
      },
      environment: {
        type: "string",
        description: "Environment name or GUID. Uses default if omitted.",
      },
      top: {
        type: "number",
        description: "Maximum number of runs to return (default 25)",
        minimum: 1,
        maximum: 250,
      },
      status: {
        type: "string",
        enum: ["Succeeded", "Failed", "Running", "Cancelled", "TimedOut", "Waiting"],
        description: "Only runs with this status. 'Failed' finds failures even when the recent page is all green.",
      },
    },
    required: ["flowId"],
  },
};

export interface FlowRunResult {
  runId: string;
  status: string;
  startTime: string;
  endTime: string | null;
  durationMs: number | null;
  triggerName: string;
  error: {
    code: string;
    message: string;
  } | null;
}

export async function handleGetRuns(
  api: FlowManagementApi,
  input: unknown
): Promise<{ text: string; structured?: Record<string, unknown> }> {
  const parsed = getRunsInputSchema.parse(input);

  logger.info({ flowId: parsed.flowId }, "Executing get_runs");

  const runs = await api.getFlowRuns(
    parsed.flowId,
    parsed.environment,
    parsed.top ?? 25,
    parsed.status
  );

  const result: FlowRunResult[] = runs.map((run) => {
    const startTime = new Date(run.properties.startTime).getTime();
    const endTime = run.properties.endTime
      ? new Date(run.properties.endTime).getTime()
      : null;

    return {
      runId: run.name,
      status: run.properties.status,
      startTime: run.properties.startTime,
      endTime: run.properties.endTime ?? null,
      durationMs: endTime !== null ? endTime - startTime : null,
      triggerName: run.properties.trigger.name,
      error: run.properties.error ?? null,
    };
  });

  const succeeded = result.filter((r) => r.status === "Succeeded").length;
  const failed = result.filter((r) => r.status === "Failed").length;
  const running = result.filter((r) => r.status === "Running").length;

  const lines = [
    parsed.status
      ? `Runs with status ${parsed.status}: ${result.length}${result.length === 0 ? " (none in this flow's history)" : ""}`
      : `Runs: ${result.length} total (${succeeded} succeeded, ${failed} failed, ${running} running)`,
  ];

  for (const run of result) {
    const duration = run.durationMs ? `${(run.durationMs / 1000).toFixed(1)}s` : "...";
    const time = new Date(run.startTime).toLocaleTimeString();
    const err = run.error ? ` - ${run.error.message}` : "";
    lines.push(`  ${run.status}: ${time} (${duration}) [${run.runId}]${err}`);
  }

  // Machine-parseable mirror of the prose, emitted as MCP structuredContent (#11).
  const structured: Record<string, unknown> = {
    count: result.length,
    succeeded,
    failed,
    running,
    runs: result,
  };

  return { text: lines.join("\n"), structured };
}
